import { cloneDeep, some, each, get } from 'lodash'
import Moment from 'moment-timezone'

export default {
  props: {
    report: {
      type: Object
    },
    context: {
      type: String,
      default: 'create'
    }
  },
  data () {
    return {
      localReport: cloneDeep(this.report),
      action: this.context,
      saving: false
    }
  },
  computed: {
    /**
     * Get the timezone that the report is operating under. If a new report,
     * default to the current customer context's timezone.
     */
    timezone () {
      return get(this.report, 'parameters.timezone', this.customerContext.timezone())
    }
  },
  methods: {
    /**
     * Load a stored report date into the form's timezone.
     *
     * Reports with `dateKeyed` set query tables stored by calendar date (customer_insights_history), and the back-end
     * reads the date straight off the string in whatever offset it was sent with. Keep that calendar date rather than
     * converting the instant, so a report saved in another timezone (e.g. the UTC ones created before VF-4345) reopens
     * with the same dates it was generated with, instead of shifting a day.
     */
    toFormDate (value) {
      if (this.dateKeyed) {
        return Moment.tz(Moment.parseZone(value).format('YYYY-MM-DDTHH:mm:ss'), this.timezone)
      }
      return Moment.tz(value, this.timezone)
    },
    async createReport () {
      this.action = 'create'
      await this.submitReport()
    },
    async duplicateReport () {
      this.action = 'duplicate'
      await this.submitReport()
    },
    async updateReport () {
      this.action = 'update'
      await this.submitReport()
    },
    /**
     * Pushes the report parameters to the back-end either creating a new report or updating an existing report
     * depending on the component's context (create, update, duplicate)
     * @return undefined
     */
    async submitReport () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$emit('report-validation-fail')
        return
      }
      try {
        await this.setLocalReportParameters(this.buildReportParameters())
        // Strip out the status. If present, it was cloned from another report and the status would be specific to that report.
        delete this.localReport.status
        // We don't need to transmit the results back to the server.
        delete this.localReport.results
        // Fire a Google Analytics event (we want this to fire regardless of whether the API request succeeds, so that we
        // have some visibility into the user's intent, regardless of success.)
        this.$gtag.event(this.context, { event_category: 'Report', event_label: this.localReport.type })
        // Submit the report, and emit an event if successful.
        const report = await this.$orm('Report').createOrUpdate(this.localReport)
        this.$emit('report-saved', report)
      } catch (error) {
        this.$emit('report-error', error)
        throw (error)
      }
    },
    async setLocalReportParameters (reportParameters) {
      // If action is 'create' or 'duplicate' then create a new ReportRecord
      if (this.action === 'create' || this.action === 'duplicate') {
        this.localReport = this.$orm('Report').createRecord({
          type: this.reportType,
          name: this.localReport.name || this.defaultReportName(),
          parameters: reportParameters
        })
      } else {
        this.localReport.parameters = reportParameters
      }
    },
    /**
     * Pre-populate the normal start and end date fields.
     */
    preSelectDates () {
      const preSelectedStartDate = get(this, 'report.parameters.startDate')
      if (preSelectedStartDate) {
        this.startDate = this.toFormDate(preSelectedStartDate)
      }
      const preSelectedEndDate = get(this, 'report.parameters.endDate')
      if (preSelectedEndDate) {
        this.endDate = this.toFormDate(preSelectedEndDate)
      }
    },
    /**
     * Pre-populate the comparison start and end date fields.
     */
    preSelectComparisonDates () {
      const preSelectedComparisonStartDate = get(this, 'report.parameters.comparisonStartDate')
      if (preSelectedComparisonStartDate) {
        this.comparisonStartDate = this.toFormDate(preSelectedComparisonStartDate)
      }
      const preSelectedComparisonEndDate = get(this, 'report.parameters.comparisonEndDate')
      if (preSelectedComparisonEndDate) {
        this.comparisonEndDate = this.toFormDate(preSelectedComparisonEndDate)
      }
    },
    /**
     * Pull the customers selected from a previous report (if defined) and set the state of this form to match.
     *
     * @var ReportRecord report
     * @return undefined
     */
    preSelectCustomers () {
      each(get(this, 'report.customer_scope'), customerId => {
        if (some(this.availableCustomers, c => c.value === customerId)) {
          this.reportCustomers.push(customerId)
        }
      })
    }
  },
  created () {
    this.populateReportForm()
  },
  watch: {
    /**
     * The report supplied can change when we clone from the ReportCreate
     * component. Watch for changes and update the form accordingly
     */
    report () {
      this.populateReportForm()
    }
  }
}
